import { DateTimeMetaData } from './date-time-meta-data';
import { EnumMetaData } from './enum-meta-data';
import { ModelVersionMetaData } from './model-version-meta-data';
import { StringMetaData } from './string-meta-data';
import { AssociationPropertyMetaData } from './association-property-meta-data';
import { CoreMetaData } from './core-meta-data';
import { BoolMetaData } from './bool-meta-data';
import { NumericMetaData } from './numeric-meta-data';
import { PropertyMetaData } from './property-meta-data';
import { Expose, Type } from '@nts/std/serialization';
import { MetaDataDescriptions } from './meta-data-descriptions';
import { MetaDataUtils } from './meta-data-utils';
import { GuidMetaData } from './guid-meta-data';
import { UserMetaData } from './user-meta-data';
import { TimeSpanMetaData } from './time-span-meta-data';
import { AggregateMetaData } from './aggregate-meta-data';
import type { AggregateMetaDataInterface, AssociationMetaDataInterface, AssociationPropertyMetaDataInterface, DateTimeMetaDataInterface, DomainModelMetaDataInterface, EnumMetaDataInterface, ExternalMetaDataInterface, GuidMetaDataInterface, InternalAssociationMetaDataInterface, InternalCollectionMetaDataInterface, InternalRelationMetaDataInterface, MetaDataDescriptionsInterface, ModelVersionMetaDataInterface, NumericMetaDataInterface, PropertyMetaDataInterface, StringMetaDataInterface, TimeSpanMetaDataInterface, UserMetaDataInterface } from '@nts/std/interfaces';
import type { BoolMetaDataInterface } from '@nts/std/interfaces';
import { CloneTypes } from '@nts/std/types';

export class DomainModelMetaData extends CoreMetaData implements DomainModelMetaDataInterface {

    @Expose()
    fullName: string;

    @Expose()
    @Type(() => InternalRelationMetaData)
    internalRelations: InternalRelationMetaDataInterface[];

    @Expose()
    @Type(() => ExternalMetaData)
    externals: ExternalMetaDataInterface[];

    @Expose()
    @Type(() => InternalCollectionMetaData)
    internalCollections: InternalCollectionMetaData[];

    @Expose()
    identityNames: string[];

    @Expose()
    @Type(() => BoolMetaData)
    bools: BoolMetaDataInterface[];

    @Expose()
    @Type(() => StringMetaData)
    strings: StringMetaDataInterface[];

    @Expose()
    @Type(() => GuidMetaData)
    guids: GuidMetaDataInterface[];

    @Expose()
    @Type(() => DateTimeMetaData)
    dateTimes: DateTimeMetaDataInterface[];

    @Expose()
    @Type(() => TimeSpanMetaData)
    timeSpans: TimeSpanMetaDataInterface[];

    @Expose()
    @Type(() => NumericMetaData)
    numerics: NumericMetaDataInterface[];

    @Expose()
    @Type(() => EnumMetaData)
    enums: EnumMetaDataInterface[];

    @Expose()
    @Type(() => ModelVersionMetaData)
    version: ModelVersionMetaDataInterface;

    @Expose()
    propertyNames: string[];

    @Expose()
    allPropertyNames: string[];

    /**
     * è una descrizione in lingua
     */
    @Expose()
    isMultiLanguage?: boolean;

    constructor() {
        super();

        this.internalRelations = [];
        this.externals = [];
        this.internalCollections = [];

        this.identityNames = [];

        this.bools = [];
        this.strings = [];
        this.dateTimes = [];
        this.numerics = [];
        this.enums = [];
        this.guids = [];
        this.timeSpans = [];

        this.propertyNames = [];
        this.allPropertyNames = [];
    }

    getPropertyMetaData(propertyName: string): PropertyMetaDataInterface {
        return [
            ...this.bools,
            ...this.strings,
            ...this.guids,
            ...this.dateTimes,
            ...this.numerics,
            ...this.enums,
            ...this.timeSpans,
            this.version
        ].find((propertyMetaData: PropertyMetaData) =>
            MetaDataUtils.toCamelCase(propertyMetaData?.name) === MetaDataUtils.toCamelCase(propertyName)
        );
    }

    getMainDescriptionProperty(): PropertyMetaDataInterface {
        return this.strings.find((s) => s.isMainDescription === true);
    }
}

export class AssociationMetaData implements AssociationMetaDataInterface {

    context?: any;

    /**
     * MetaDati che dipendono dall'utente corrente
     */
    @Expose()
    @Type(() => UserMetaData)
    userMetaData?: UserMetaDataInterface;

    @Expose()
    @Type(() => MetaDataDescriptions)
    descriptions: MetaDataDescriptionsInterface;

    @Expose()
    principalPropertyName: string;

    get name() {
        return this.principalPropertyName;
    }

    @Expose()
    @Type(() => DomainModelMetaData)
    principalMetaData: DomainModelMetaDataInterface;

    @Expose()
    @Type(() => AssociationPropertyMetaData)
    associationProperties: AssociationPropertyMetaDataInterface[];

    @Expose()
    position: number;

    @Expose()
    isExtension?: boolean;

    constructor(
        userMetaData?: UserMetaDataInterface,
        descriptions?: MetaDataDescriptionsInterface,
        principalPropertyName?: string,
        principalMetaData?: DomainModelMetaDataInterface,
        associationProperties?: AssociationPropertyMetaDataInterface[],
        position?: number,
    ) {
        this.userMetaData = userMetaData;
        this.descriptions = descriptions ?? new MetaDataDescriptions();
        this.principalPropertyName = principalPropertyName;
        this.principalMetaData = principalMetaData ?? new DomainModelMetaData();
        this.associationProperties = associationProperties ?? [];
        this.position = position;
    }
}
export class ExternalMetaData extends AssociationMetaData implements ExternalMetaDataInterface {

    @Expose()
    @Type(() => AggregateMetaData)
    dependentAggregateMetaData: AggregateMetaDataInterface;

    // Indica se l'external è obbligatoria
    @Expose()
    isRequired: boolean;

    @Expose()
    isRemote: boolean;

    @Expose()
    cloneType: CloneTypes;

    /**
    * Se una external dipende da un'altra external o property, è il nome della property da cui dipende
    * Es.: External Zona ed external SottoZona se Zona e padre di SottoZona e si vogliono attivare degli automatismi allora ParentExternalPropertyName dovrà essere ZonaRef (valorizzato con il path a partire dalla root).
    */
    @Expose()
    parentIdentityPropertyPathName?: string;

    constructor(
        userMetaData?: UserMetaDataInterface,
        descriptions?: MetaDataDescriptionsInterface,
        principalPropertyName?: string,
        principalMetaData?: DomainModelMetaDataInterface,
        associationProperties?: AssociationPropertyMetaDataInterface[],
        position?: number,
        dependentAggregateMetaData?: AggregateMetaDataInterface,
        isRequired?: boolean,
        isRemote?: boolean,
        cloneType?: CloneTypes
    ) {
        super(
            userMetaData,
            descriptions,
            principalPropertyName,
            principalMetaData,
            associationProperties,
            position
        );
        this.dependentAggregateMetaData = dependentAggregateMetaData;
        this.isRequired = isRequired;
        this.isRemote = isRemote;
        this.cloneType = cloneType;
    }
}

export class InternalAssociationMetaData extends AssociationMetaData implements InternalAssociationMetaDataInterface {

    @Expose()
    @Type(() => DomainModelMetaData)
    dependentMetaData: DomainModelMetaDataInterface;
}

export class InternalCollectionMetaData extends InternalAssociationMetaData implements InternalCollectionMetaDataInterface {
    isUniqueFunc?: (val) => any;
    uniqueFields?: string[];
}

export class InternalRelationMetaData extends InternalAssociationMetaData implements InternalRelationMetaDataInterface {

}

